import { useEffect, useMemo, useState, useCallback } from "react";
import { contactAPI } from "../../lib/api";
import { toast } from "sonner";
import { Mail, Phone, CheckCheck, Search, Inbox } from "lucide-react";
import { format } from "date-fns";
import { ro } from "date-fns/locale";

const ContactMessagesPage = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");

  const fetchMessages = useCallback(async () => {
    try {
      const response = await contactAPI.getMessages();
      setMessages(response.data);
    } catch (error) {
      toast.error("Eroare la încărcarea mesajelor");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  const filteredMessages = useMemo(() => {
    return messages.filter((message) => {
      const matchesFilter = filter === "all" ? true : filter === "unread" ? !message.is_read : !!message.is_read;
      const haystack = `${message.name} ${message.email} ${message.phone || ""} ${message.message}`.toLowerCase();
      return matchesFilter && haystack.includes(search.toLowerCase());
    });
  }, [messages, filter, search]);

  const handleMarkRead = async (id) => {
    try {
      await contactAPI.markRead(id);
      setMessages((current) => current.map((m) => m.id === id ? { ...m, is_read: true } : m));
      toast.success("Marcat ca citit");
    } catch (error) {
      toast.error("Eroare sistem");
    }
  };

  const unreadCount = messages.filter((m) => !m.is_read).length;

  // Funcție antiglonț pentru a preveni crash-ul "Invalid time value" la mesajele vechi
  const safeDateRender = (dateString) => {
    if (!dateString) return "Dată necunoscută";
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return "Dată invalidă";
    return format(d, "dd MMM yyyy, HH:mm", { locale: ro });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-4xl font-bold text-white uppercase tracking-tight">Inbox Contact</h1>
          <p className="text-white/40 text-sm mt-1">Cereri venite de pe site-ul public</p>
        </div>
        <div className="bg-white/5 border border-white/10 px-4 py-2.5 flex items-center gap-3 rounded">
          <span className="text-xs font-bold text-white/50 uppercase tracking-wider">Mesaje Noi:</span>
          <span className="text-[#6db025] font-heading font-bold text-lg">{unreadCount}</span>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 bg-[#0A0A0A] p-4 border border-white/5 rounded">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30" size={16} />
          <input
            type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Caută în mesaje..."
            className="w-full bg-[#121212] border border-white/10 pl-11 pr-4 py-2.5 text-white focus:border-[#6db025] outline-none transition-colors placeholder:text-white/20 rounded text-sm"
          />
        </div>
        <select
          value={filter} onChange={(e) => setFilter(e.target.value)}
          className="bg-[#121212] border border-white/10 px-4 py-2.5 text-white/60 text-xs font-bold uppercase tracking-wider outline-none focus:border-[#6db025] rounded cursor-pointer"
        >
          <option value="all" className="bg-[#121212] text-white">Istoric Complet</option>
          <option value="unread" className="bg-[#121212] text-white">Doar Necitite</option>
          <option value="read" className="bg-[#121212] text-white">Deja Citite</option>
        </select>
      </div>

      {loading ? (
         <div className="bg-[#0A0A0A] border border-white/5 p-16 text-center text-[#6db025] animate-pulse font-heading text-xs tracking-widest uppercase rounded">Sincronizare Inbox...</div>
      ) : filteredMessages.length === 0 ? (
        <div className="bg-[#0A0A0A] border border-white/5 p-20 text-center flex flex-col items-center justify-center gap-4 opacity-30 rounded">
           <Inbox size={40} className="text-white" />
           <p className="text-white text-xs font-bold uppercase tracking-widest">Inbox Gol</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredMessages.map((msg) => (
            <div key={msg.id} className={`relative bg-[#0F0F0F] border border-white/5 hover:border-white/10 hover:bg-[#121212] transition-colors rounded overflow-hidden ${msg.is_read ? 'opacity-80' : ''}`}>
              <div className={`absolute left-0 top-0 bottom-0 w-[3px] ${msg.is_read ? 'bg-white/10' : 'bg-[#6db025]'}`} />
              <div className="p-5 pl-6">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <h3 className={`text-base font-bold uppercase tracking-tight ${!msg.is_read ? 'text-white' : 'text-white/60'}`}>{msg.name}</h3>
                      {!msg.is_read && <span className="text-[10px] uppercase px-2 py-0.5 bg-[#6db025]/20 text-[#6db025] font-black tracking-widest rounded">Mesaj Nou</span>}
                    </div>

                    <div className="flex flex-wrap items-center gap-x-5 gap-y-1 mb-3 text-[11px] font-bold uppercase tracking-widest text-white/40">
                      <span className="flex items-center gap-1.5"><Mail size={13} className="text-[#6db025]/60" /> {msg.email}</span>
                      {msg.phone && <span className="flex items-center gap-1.5"><Phone size={13} className="text-[#6db025]/60" /> {msg.phone}</span>}
                    </div>

                    <div className="bg-[#050505] border border-white/5 p-4 text-white/70 text-sm leading-relaxed whitespace-pre-wrap font-medium rounded">
                      {msg.message}
                    </div>
                    <div className="mt-2 text-[11px] text-white/30 uppercase font-bold tracking-widest">
                       Primit la: {safeDateRender(msg.created_at)}
                    </div>
                  </div>

                  {!msg.is_read && (
                    <button onClick={() => handleMarkRead(msg.id)} className="flex items-center gap-2 px-4 py-2.5 bg-[#6db025]/10 border border-[#6db025]/20 text-[#6db025] hover:bg-[#6db025] hover:text-black transition-colors rounded text-[11px] font-black uppercase tracking-widest self-start whitespace-nowrap">
                      <CheckCheck size={16} /> Finalizat
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ContactMessagesPage;